import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { filter, map } from 'rxjs';

import { STAFF_ROLE_LABELS, StaffRole, ThemeService } from '@gisgo/shared';

import { AuthStore } from '../auth/auth.store';
import { OpsBadges } from '../realtime/ops-badges';
import { TopbarSlots } from './topbar-slots';

interface NavEntry {
  readonly label: string;
  readonly icon: string;
  readonly path: string;
  /** Rôles autorisés (admin passe toujours). */
  readonly roles: readonly StaffRole[];
}

interface NavSection {
  readonly title: string;
  readonly entries: readonly NavEntry[];
}

/** Shell Gisgo Ops — sidebar + topbar (mockup design-preview, tous les frames). */
@Component({
  selector: 'app-shell',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule,
    MatListModule,
    MatSidenavModule,
    MatToolbarModule,
    MatTooltipModule,
    NgTemplateOutlet,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
  ],
  templateUrl: './shell.html',
  styles: `
    /* Pas de backtick dans ces commentaires : il terminerait le template
       literal de styles (piège documenté dans CLAUDE.md).

       CHAÎNE DE HAUTEURS — html/body 100% > app-root 100% > CE composant >
       mat-sidenav-container. Sans hauteur explicite ici, le container (qui
       est en overflow:hidden) s'effondre sur son contenu et les cartes
       Leaflet se retrouvent à 0 px. */
    :host {
      display: block;
      height: 100%;
    }

    .gs-shell {
      height: 100%;
    }

    /* ⚠️ Ces règles DOIVENT vivre ici et pas en utilities Tailwind : les
       styles de Material ne sont pas dans un layer, les utilities si — donc
       display:block de .mat-drawer-content bat un .grid Tailwind, quelle que
       soit la spécificité. */
    .gs-content {
      display: grid;
      grid-template-rows: var(--ops-topbar-h) 1fr;
      min-width: 0;
      /* Material met overflow:auto ; c'est le main qui scrolle, pas lui. */
      overflow: hidden;
    }

    /* Le contenu d'un mat-sidenav est enveloppé dans un conteneur interne :
       la colonne flex doit donc être posée sur NOTRE div, pas sur l'hôte. */
    .gs-sidebar-inner {
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-height: 100%;
      padding: var(--space-4) var(--space-3);
    }

    /* mat-toolbar est déjà en flex : il ne manque que la gouttière et le
       filet bas des frames. */
    .gs-topbar {
      gap: var(--space-4);
      padding: 0 var(--space-5);
      border-bottom: 1px solid var(--outline-variant);
    }

    /* Entrée active : la pastille est pleine --primary (noire en clair), mais
       Material n'expose AUCUN token pour le libellé et l'icône de l'état
       activé — seulement pour le conteneur. Sans ça le texte reste en
       --on-surface-variant, soit un gris foncé sur fond noir : contraste
       mesuré 2,8:1, illisible.

       On repose donc les tokens de couleur sur NOTRE classe (posée par le même
       routerLinkActive que [activated]) plutôt que de viser le
       .mdc-list-item--activated interne. Les variantes hover et focus sont
       incluses, sinon survoler l'entrée active y ramènerait le gris.

       (Pas de backticks dans ce bloc : il vit dans le template literal de
       styles, ils le refermeraient — comme les commentaires au-dessus.) */
    .gs-nav-active {
      --mat-list-list-item-label-text-color: var(--on-primary);
      --mat-list-list-item-leading-icon-color: var(--on-primary);
      --mat-list-list-item-hover-label-text-color: var(--on-primary);
      --mat-list-list-item-hover-leading-icon-color: var(--on-primary);
      --mat-list-list-item-focus-label-text-color: var(--on-primary);
    }

    /* Badge de compteur (incidents, KYC). Material habille le slot meta via
       .mdc-list-item--with-trailing-meta .mdc-list-item__end : deux classes,
       et non layered. Les utilities Tailwind text-on-error / font-extrabold
       ne pesaient donc rien — le compteur sortait en --on-surface-variant,
       gris foncé sur le rond rouge, contraste mesuré 1,6:1.

       Les tokens du slot sont la seule prise qui gagne cette cascade. Le fond,
       le rayon et les paddings restent en utilities : Material ne les touche
       pas, eux s'appliquent bien. */
    .gs-nav-badge {
      --mat-list-list-item-trailing-supporting-text-color: var(--on-error);
      --mat-list-list-item-trailing-supporting-text-size: 11px;
      --mat-list-list-item-trailing-supporting-text-weight: var(--weight-extrabold);
    }

    /* Cadenas des entrées verrouillées (RBAC). Même slot, donc même cascade :
       le text-[16px] qui était posé ici ne pesait rien et l'icône sortait à la
       taille du slot, 11px. */
    .gs-nav-lock {
      --mat-list-list-item-trailing-supporting-text-size: 16px;
    }
  `,
})
export class Shell {
  private readonly router = inject(Router);
  protected readonly auth = inject(AuthStore);
  protected readonly themeService = inject(ThemeService);
  protected readonly slots = inject(TopbarSlots);

  protected readonly roleLabels = STAFF_ROLE_LABELS;

  private readonly badges = inject(OpsBadges);

  /** Badge incidents live (private-ops.incidents). */
  protected readonly openIncidents = this.badges.openIncidents;
  /** Badge KYC en attente (GET /ops/drivers). */
  protected readonly pendingKyc = this.badges.pendingKyc;
  /** Badge commandes à valider (private-ops.orders). */
  protected readonly ordersToValidate = this.badges.ordersToValidate;

  constructor() {
    this.badges.connect();
  }

  protected readonly sections = computed<readonly NavSection[]>(() => [
    {
      title: 'Supervision',
      entries: [
        { label: 'Console', icon: 'map', path: '/supervision/console', roles: ['dispatcher'] },
        {
          label: 'Commandes',
          icon: 'receipt_long',
          path: '/supervision/commandes',
          roles: ['dispatcher'],
        },
        { label: 'Flotte', icon: 'radar', path: '/supervision/flotte', roles: ['dispatcher'] },
        {
          label: 'Incidents',
          icon: 'report',
          path: '/supervision/incidents',
          roles: ['dispatcher'],
        },
        {
          label: 'Dashboard',
          icon: 'monitoring',
          path: '/supervision/dashboard',
          roles: ['dispatcher'],
        },
      ],
    },
    {
      title: 'Administration',
      entries: [
        // dispatcher en lecture seule (décision C1) — l'écran désactive ses actions
        {
          label: 'Livreurs',
          icon: 'badge',
          path: '/administration/livreurs',
          roles: ['dispatcher'],
        },
        // admin uniquement (décisions D1/E1) — visibles verrouillées pour les autres
        {
          label: 'Utilisateurs',
          icon: 'group',
          path: '/administration/utilisateurs',
          roles: [],
        },
        {
          label: 'Business',
          icon: 'domain',
          path: '/administration/business',
          roles: [],
        },
      ],
    },
    {
      // admin only (feature F) — visibles verrouillées pour les autres
      title: 'Référentiels',
      entries: [
        {
          label: 'Zones de livraison',
          icon: 'location_on',
          path: '/administration/zones',
          roles: [],
        },
        {
          label: 'Catégories',
          icon: 'category',
          path: '/administration/categories',
          roles: [],
        },
        {
          label: 'Verticales',
          icon: 'shopping_basket',
          path: '/administration/verticales',
          roles: [],
        },
      ],
    },
    {
      title: 'Commercial',
      entries: [
        {
          label: 'Restaurants',
          icon: 'storefront',
          path: '/commercial/restaurants',
          roles: ['sales'],
        },
        { label: 'Pipeline', icon: 'trending_up', path: '/commercial/pipeline', roles: ['sales'] },
        // admin only (feature F) — levier marketing, verrouillé pour sales
        { label: 'Codes promo', icon: 'percent', path: '/administration/promos', roles: [] },
      ],
    },
  ]);

  protected readonly pageTitle = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => {
        let route = this.router.routerState.snapshot.root;
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route.title ?? 'Gisgo Ops';
      }),
    ),
    { initialValue: 'Gisgo Ops' },
  );

  /** Console & flotte : la carte est plein-bord, le main ne padde/scrolle pas. */
  protected readonly fullBleed = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => {
        let route = this.router.routerState.snapshot.root;
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route.data['fullBleed'] === true;
      }),
    ),
    { initialValue: false },
  );

  /** RBAC visible : les entrées non autorisées sont verrouillées, pas masquées. */
  protected canAccess(entry: NavEntry): boolean {
    const role = this.auth.role();
    return role === 'admin' || (role !== null && entry.roles.includes(role));
  }

  protected badgeFor(entry: NavEntry): number {
    switch (entry.path) {
      case '/supervision/incidents':
        return this.openIncidents();
      case '/supervision/commandes':
        return this.ordersToValidate();
      case '/administration/livreurs':
        return this.pendingKyc();
      default:
        return 0;
    }
  }
}
